import React, { useCallback, useEffect, useState } from "react";
import "./PricingStep.scss";
import {
  InfoCircleOutlined,
  PlusOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import {
  Col,
  Collapse,
  Divider,
  Input,
  InputNumber,
  InputNumberProps,
  InputProps,
  Radio,
  Row,
  Select,
  SelectProps,
  Space,
} from "antd";
import Checkbox from "antd/es/checkbox/Checkbox";
import PriceSlider from "@components/PriceSlider/PriceSlider";
import { IProductForm } from "@utils/Interfaces";
import Validates from "@utils/Validates";
import crownImg from "@assets/icons/crown.svg";
import debit from "@assets/icons/debit.svg";
import paypalRec from "@assets/icons/paypalRec.svg";
import visa from "@assets/icons/visa.svg";
import mastercard from "@assets/icons/mastercard.svg";
import amex from "@assets/icons/american-express.svg";
import { useUnit } from "contexts/UnitContext";
import Helpers from "@utils/Helpers";
import { useUser } from "contexts/UserContext";
import { errorLog } from "logging";
import { paymentApi } from "services/PaymentApi";
import cash from "@assets/icons/cash.png";
import PaymentModal from "@components/PaymentModal/PaymentModal";
import OfflinePaymentDisclaimerModal, {
  OFFLINE_PAYMENT_METHOD_IDS,
} from "@components/OfflinePaymentDisclaimerModal/OfflinePaymentDisclaimerModal";

const inputProps: InputProps & SelectProps & InputNumberProps = {
  size: "large",
  variant: "filled",
};
const { Option } = Select;

interface IPricingStep {
  productForm: IProductForm;
  setProductForm: (value: any) => void;
  metrics: any;
  selectedWeightUnit: any;
  handleChangeUnit: (key: string, unit: string) => void;
  isEstimate: boolean;
  setIsEstimate: (value: boolean) => void;
  handleUndoAll: () => void;
}

// const cardIcons: Record<string, string> = {
//   visa: visa,
//   mastercard: mastercard,
//   paypal: paypalRec,
//   amex: amex,
// };

const PricingStep: React.FC<IPricingStep> = ({
  productForm,
  setProductForm,
  metrics,
  selectedWeightUnit,
  handleChangeUnit,
  isEstimate,
  setIsEstimate,
  handleUndoAll,
}) => {
  const { units, convertCurrency, convertWeight } = useUnit();
  const [unitPrice, setUnitPrice] = useState<any>(
    productForm.hay_unit == "Ton" ? 1 : 2
  );
  const [allPayment, setAllPayment] = useState<any>(null);
  const [addCardModal, setAddCardModal] = useState(false);
  const [isPriceEstimated, setIsPriceEstimated] = useState(false);
  const [pendingOfflinePaymentId, setPendingOfflinePaymentId] = useState<
    number | null
  >(null);

  const handleUpdateProductForm = (key: string, value: any) => {
    if (
      key === "current_price" ||
      key === "price_per_tons" ||
      key === "weight_per_bale"
    ) {
      value = Validates.validateNumberForm(value);
    }

    if (key === "weight_per_bale") {
      const newTotalWeight = value * parseFloat(productForm.hay_availability);
      const totalPrice =
        value *
        parseFloat(productForm.hay_availability) *
        productForm.price_per_tons;
      setProductForm({
        ...productForm,
        weight_per_bale: value,
        total_weight: newTotalWeight.toFixed(2),
        current_price: totalPrice.toFixed(2),
      });
      setIsEstimate(false);
      return;
    }
    setProductForm({
      ...productForm,
      [key]: value,
    });
  };

  const updatePaymentSelection = (isChecked: boolean, value: number) => {
    if (isChecked) {
      const newOptionTypeArr = [...productForm.payment_info_ids, value];
      handleUpdateProductForm("payment_info_ids", newOptionTypeArr);
    } else {
      const newOptionTypeArr = productForm.payment_info_ids.filter(
        (item) => item !== value
      );
      handleUpdateProductForm("payment_info_ids", newOptionTypeArr);
    }
  };

  const handleSelectPayment = (isChecked: boolean, value: number) => {
    if (isChecked && OFFLINE_PAYMENT_METHOD_IDS.has(value)) {
      setPendingOfflinePaymentId(value);
      return;
    }

    updatePaymentSelection(isChecked, value);
  };

  const handleAcceptOfflinePayment = () => {
    if (pendingOfflinePaymentId === null) {
      return;
    }

    updatePaymentSelection(true, pendingOfflinePaymentId);
    setPendingOfflinePaymentId(null);
  };

  useEffect(() => {
    const getAllPayment = async () => {
      try {
        const response = await paymentApi.getAllPayment();
        if (response.status === 200) {
          setAllPayment(response.data.data);
        }
      } catch (error) {
        errorLog("PaymentMethod.tsx - getAllPayment", error);
      } finally {
      }
    };
    if (!allPayment) {
      getAllPayment();
    }
  }, [allPayment]);

  const handleSetPrice = (value: any) => {
    value = Validates.validateNumberForm(value);
    if (unitPrice == 1) {
      const totalPrice = parseFloat(productForm.total_weight) * Number(value);
      const pricePerBale =
        Number(value) * parseFloat(productForm.weight_per_bale);

      setProductForm({
        ...productForm,
        price_per_tons: Number(value),
        price_per_bale: parseFloat(pricePerBale.toFixed(2) || "0"),
        current_price: parseFloat(totalPrice.toFixed(2) || "0"),
      });
    } else {
      const pricePerTon =
        Number(value) / parseFloat(productForm.weight_per_bale);
      const totalPrice =
        Number(value) * parseFloat(productForm.hay_availability);
      setProductForm({
        ...productForm,
        price_per_tons: parseFloat(pricePerTon.toFixed(2) || "0"),
        price_per_bale: Number(value),
        current_price: parseFloat(totalPrice.toFixed(2) || "0"),
      });
    }
    setIsPriceEstimated(false);
  };

  return (
    <div className="PricingStep">
      <div className="step-desc text-muted mb-2">
        Here's a suggested price range based on your information.{" "}
        <InfoCircleOutlined />
      </div>
      <div className="forms-group">
        <div className="forms-title">Unit Price ({productForm.hay_type})</div>
        <div className="form-group">
          <Radio.Group
            onChange={(e) => {
              const value = e.target.value;
              handleUpdateProductForm("hay_unit", value == 1 ? "Ton" : "Bale");
              setUnitPrice(e.target.value);
            }}
            value={unitPrice}
          >
            <Radio value={2}>
              Price/bale:{" "}
              {productForm.shape == "ssquare"
                ? "Small Square"
                : productForm.shape == "lsquare"
                ? "Large Square"
                : "Round"}
            </Radio>
            <Radio value={1}>Price/ton</Radio>
          </Radio.Group>
        </div>
      </div>
      <div className="price-slider-wrap">
        <PriceSlider
          unit={unitPrice}
          type="createProduct"
          productForm={productForm}
          metrics={unitPrice == 1 ? metrics.pertons : metrics.per_bale}
          setProductForm={setProductForm}
        />
      </div>
      <Row gutter={16}>
        <Col span={12}>
          <div className="form-group">
            <div className="form-title wrap">
              Price/{unitPrice === 1 ? "Ton" : "Bale"}
            </div>
            <Input
              className={`${!isPriceEstimated ? "customized" : ""}`}
              {...inputProps}
              value={
                unitPrice === 1
                  ? Helpers.formatNumberInput(productForm.price_per_tons)
                  : Helpers.formatNumberInput(productForm.price_per_bale)
              }
              onChange={(e: any) => {
                handleSetPrice(e.target.value);
              }}
              autoFocus
              prefix={"$"}
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="form-group">
            <div className="form-title">Total Price</div>
            <Input
              className="input-number"
              controls={false}
              {...inputProps}
              value={Helpers.formatNumberInput(productForm.current_price)}
              onChange={(e: any) => {
                const value = Validates.validateNumberForm(e.target.value);
                const pricePerTon =
                  Number(value) / parseFloat(productForm.total_weight);
                setProductForm({
                  ...productForm,
                  price_per_tons: parseFloat(pricePerTon.toFixed(1)),
                  current_price: parseFloat(value),
                });
              }}
              prefix={"$"}
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="form-group">
            <div className="form-title wrap">
              Weight/Bale <InfoCircleOutlined className="info-btn" />
              <span className={`sub-label ${!isEstimate ? "customized" : ""}`}>
                {isEstimate ? "Estimated" : "Customized"}
              </span>
            </div>
            <Input
              {...inputProps}
              className={`${!isEstimate ? "customized" : ""}`}
              value={productForm.weight_per_bale}
              onChange={(e) => {
                const value = e.target.value;
                handleUpdateProductForm("weight_per_bale", value);
              }}
              addonAfter={
                <>
                  <Select
                    className="weight-select"
                    value={selectedWeightUnit.weightPerBale}
                    onChange={(value) =>
                      handleChangeUnit("weightPerBale", value)
                    }
                  >
                    <Option value="lb">lb</Option>
                    <Option value="kg">kg</Option>
                    <Option value="t">ton</Option>
                  </Select>
                  <button
                    className="undo-btn"
                    disabled={isEstimate}
                    onClick={handleUndoAll}
                  >
                    <UndoOutlined />
                  </button>
                </>
              }
            />
          </div>
        </Col>
        <Col span={12}>
          <div className="form-group">
            <div className="form-title wrap">
              Total Weight <InfoCircleOutlined className="info-btn" />
              <span className="sub-label">Estimated</span>
            </div>
            <Input
              {...inputProps}
              suffix={"ton"}
              value={productForm.total_weight}
              disabled
            />
          </div>
        </Col>
      </Row>
      <div className="forms-group">
        <div className="forms-title">
          Shipping Options<span className="text-danger">*</span>
        </div>
        <Space className="w-100" direction="vertical">
          <Collapse
            size="large"
            className="shipping-option-collapse"
            collapsible="icon"
            expandIconPosition={"end"}
            items={[
              {
                key: "1",
                label: (
                  <CollapseHeader
                    text="Consumer Pick-up"
                    productForm={productForm}
                    updateProduct={handleUpdateProductForm}
                    shippingKey="consumer_pickup"
                  />
                ),
                children: (
                  <ShippingContent
                    type={"consumer"}
                    productForm={productForm}
                    updateProduct={handleUpdateProductForm}
                  />
                ),
              },
            ]}
          />
          <Collapse
            size="large"
            className="shipping-option-collapse"
            collapsible="icon"
            expandIconPosition={"end"}
            items={[
              {
                key: "2",
                label: (
                  <CollapseHeader
                    text="Delivered"
                    productForm={productForm}
                    updateProduct={handleUpdateProductForm}
                    shippingKey="shipping_producer"
                  />
                ),
                children: (
                  <ShippingContent
                    type={"producer"}
                    productForm={productForm}
                    updateProduct={handleUpdateProductForm}
                  />
                ),
              },
            ]}
          />
          <Collapse
            size="large"
            className="shipping-option-collapse"
            collapsible="disabled"
            expandIconPosition={"end"}
            items={[
              {
                key: "3",
                // label: (
                //   <CollapseHeader
                //     text="Carrier Shipping"
                //     productForm={productForm}
                //     updateProduct={handleUpdateProductForm}
                //     shippingKey="carrier_shipping"
                //   />
                // ),
                label: "Carrier Shipping",
                children: (
                  <ShippingContent
                    type={"carrier"}
                    productForm={productForm}
                    updateProduct={handleUpdateProductForm}
                  />
                ),
              },
            ]}
          />
        </Space>
      </div>
      <div className="forms-group">
        <div className="forms-title">
          Payment Options<span className="text-danger">*</span>
        </div>
        <Space className="w-100" direction="vertical">
          <div className="payment-option-item">
            <Checkbox
              className="align-items-center"
              checked={productForm.payment_info_ids.includes(5)}
              onChange={(e) => handleSelectPayment(e.target.checked, 5)}
            >
              <div className="payment-item">
                <img src={cash} alt="Cash" width={32} /> Cash
              </div>
            </Checkbox>
          </div>
          <div className="payment-option-item">
            <Checkbox
              className="align-items-center"
              checked={productForm.payment_info_ids.includes(1)}
              onChange={(e) => handleSelectPayment(e.target.checked, 1)}
            >
              <div className="payment-item">
                <img src={debit} alt="Cash" width={32} /> Debit/Credit Card
              </div>
            </Checkbox>
          </div>
          {/* {allPayment?.map((item: any) => (
            <div className="payment-option-item" key={item.payment_info_id}>
              <Checkbox
                checked={productForm.payment_info_ids.includes(
                  item.payment_info_id
                )}
                onChange={(e) =>
                  handleSelectPayment(e.target.checked, item.payment_info_id)
                }
              >
                <div className="payment-item">
                  <img
                    src={
                      cardIcons[
                        item.payment_type === "cash"
                          ? item.payment_type
                          : item?.card_type
                      ] ?? debit
                    }
                    alt={`${item?.card_type}`}
                    width={32}
                  />{" "}
                  <div className="payment-info">
                    {item.card_type !== "cash"
                      ? Helpers.hideCardNumber(item?.card_number ?? "")
                      : "Paypal"}
                    {item.card_type !== "cash" ? (
                      ""
                    ) : (
                      <span>********{item.paypal_email?.slice(-10)}</span>
                    )}
                  </div>
                </div>
              </Checkbox>
            </div>
          ))} */}
          <div className="new-payment" onClick={() => setAddCardModal(true)}>
            <PlusOutlined />
            Additional Payment Methods
          </div>
          <PaymentModal
            isOpenNewCard={addCardModal}
            setIsOpenNewCard={setAddCardModal}
            setAllPayment={setAllPayment}
          />
        </Space>
      </div>
      <div className="forms-group">
        <div className="forms-title">Return Options</div>
        <Row gutter={16}>
          <Col span={12}>
            <div className="form-group mb-3">
              <div className="form-title">Buyer Must Start a Return within</div>
              <Select
                {...inputProps}
                value={productForm.return_within}
                onChange={(value) =>
                  handleUpdateProductForm("return_within", value)
                }
              >
                <Option value="7">7 Days</Option>
                <Option value="14">14 Days</Option>
                <Option value="30">30 Days</Option>
                <Option value="60">60 Days</Option>
              </Select>
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group mb-3">
              <div className="form-title">Return Shipping will be Paid by</div>
              <Select
                {...inputProps}
                value={productForm.return_shipping_by}
                onChange={(value) =>
                  handleUpdateProductForm("return_shipping_by", value)
                }
              >
                <Option value="buyer">Buyer</Option>
                <Option value="seller">Seller</Option>
              </Select>
            </div>
          </Col>
        </Row>
        <Checkbox>Replacement or Exchange Available</Checkbox>
      </div>
      <div className="forms-group">
        <div className="forms-title">Cancellation Policy:</div>
        <Space className="w-100" direction="vertical">
          <Radio.Group
            className="cancel-option-item"
            defaultValue="full"
            options={[
              {
                value: "full",
                label: (
                  <div className="radio-item">
                    <div className="item-title">Full Refund</div>
                    <div className="item-description">
                      Buyers can get a full refund if they cancel before
                      shipping
                    </div>
                  </div>
                ),
              },
              {
                value: "reduction",
                label: (
                  <div className="radio-item">
                    <div className="item-title">20% Refund Reduction</div>
                    <div className="item-description">
                      Buyers will receive a refund reduced by 20% if they cancel
                      before shipping
                    </div>
                  </div>
                ),
              },
              {
                value: "noRefund",
                label: (
                  <div className="radio-item">
                    <div className="item-title">No Refund</div>
                    <div className="item-description">
                      Buyers can not get a refund
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </Space>
      </div>
      <OfflinePaymentDisclaimerModal
        open={pendingOfflinePaymentId !== null}
        onAccept={handleAcceptOfflinePayment}
        onCancel={() => setPendingOfflinePaymentId(null)}
      />
    </div>
  );
};

interface ICollapseHeader {
  text: string;
  updateProduct: (key: string, value: any) => void;
  productForm: IProductForm;
  shippingKey: string;
}
const CollapseHeader: React.FC<ICollapseHeader> = ({
  text,
  updateProduct,
  productForm,
  shippingKey,
}) => {
  const handleChecked = (isChecked: boolean) => {
    if (isChecked) {
      const newOpionsList = {
        ...productForm.shipping_options_details,
        [shippingKey]:
          shippingKey === "consumer_pickup"
            ? true
            : shippingKey === "shipping_producer"
            ? 4.35
            : [
                { unit: "CN Rail", cost: 4.0 },
                { unit: "Day & Ross", cost: 4.45 },
                { unit: "J.B. Hunt", cost: 6.12 },
                { unit: "FedEx", cost: 10.11 },
              ],
      };
      updateProduct("shipping_options_details", newOpionsList);
    } else {
      const newOpionsList: any = { ...productForm.shipping_options_details };
      delete newOpionsList[shippingKey];
      updateProduct("shipping_options_details", newOpionsList);
    }
  };
  return (
    <div className="CollapseHeader">
      <Checkbox
        checked={Object.keys(productForm.shipping_options_details).includes(
          shippingKey
        )}
        onChange={(e) => handleChecked(e.target.checked)}
      >
        {text}
      </Checkbox>
    </div>
  );
};

const ShippingContent: React.FC<any> = ({
  type,
  productForm,
  updateProduct,
}) => {
  const inputProps: InputProps & InputNumberProps = {
    variant: "filled",
    size: "large",
  };
  const { userProfile } = useUser();
  const { units } = useUnit();

  const handleSetShippingCost = (value: any) => {
    const newShippingOptions = structuredClone(
      productForm.shipping_options_details
    );
    if (value <= 0) {
      delete newShippingOptions.shipping_producer;
    } else {
      newShippingOptions.shipping_producer = Number(value);
    }
    updateProduct("shipping_options_details", newShippingOptions);
  };

  const handleSetCarrierShipping = (
    key: string,
    checked: boolean,
    cost: number
  ) => {
    if (checked) {
      const newShippingOptions = structuredClone(
        productForm.shipping_options_details
      );
      if (!newShippingOptions.carrier_shipping) {
        newShippingOptions.carrier_shipping = [];
      }
      newShippingOptions.carrier_shipping.push({
        unit: key,
        cost: cost,
      });
      updateProduct("shipping_options_details", newShippingOptions);
    } else {
      const newCarrierShipping =
        productForm.shipping_options_details.carrier_shipping.filter(
          (item: any) => item.unit !== key
        );

      if (newCarrierShipping.length == 0) {
        const newOpionsList: any = { ...productForm.shipping_options_details };
        delete newOpionsList.carrier_shipping;
        updateProduct("shipping_options_details", newOpionsList);
        console.log(newOpionsList);
        return;
      }

      updateProduct("shipping_options_details", {
        ...productForm.shipping_options_details,
        carrier_shipping: newCarrierShipping,
      });
    }
  };

  return (
    <div className="ShippingContent">
      <div className="farm-info">
        <Row gutter={[14, 14]}>
          <Col span={12}>
            <div className="form-group">
              <div className="title">
                Farm Name<span className="text-danger">*</span>
              </div>
              <Input
                {...inputProps}
                disabled
                value={userProfile?.farm_info.farm_name || ""}
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="form-group">
              <div className="title">Phone Number</div>
              <Input
                {...inputProps}
                disabled
                value={userProfile?.phone || ""}
              />
            </div>
          </Col>
          <Col span={24}>
            <div className="form-group">
              <div className="title">Street Address</div>
              <Input
                {...inputProps}
                disabled
                value={productForm.address?.street}
              />
            </div>
          </Col>
        </Row>
      </div>
      {type === "producer" && (
        <>
          <Divider className="divider" />
          <div className="cost-info">
            <div className="form-group">
              <div className="title">
                Shipping Cost<span className="text-danger">*</span>
              </div>
              <InputNumber
                className="shipping-cost-input"
                onChange={(value) => handleSetShippingCost(value)}
                value={
                  Object.keys(productForm.shipping_options_details).includes(
                    "shipping_producer"
                  )
                    ? productForm.shipping_options_details.shipping_producer
                    : 0
                }
                {...inputProps}
                suffix={`${units.currency}/${units.distance}`}
                controls={false}
              />
            </div>
          </div>
        </>
      )}
      {type === "carrier" && (
        <>
          <Divider className="divider" />
          <div className="cost-info">
            <Row gutter={[14, 14]}>
              <Col span={24}>
                <div className="form-group">
                  <div className="title-bold mb-2">Available Carriers</div>
                  <Space className="w-100" direction="vertical">
                    <div className="carrier-option-item">
                      <Checkbox
                        checked={productForm.shipping_options_details?.carrier_shipping?.find(
                          (item: any) => item.unit === "CN Rail"
                        )}
                        onChange={(e) =>
                          handleSetCarrierShipping(
                            "CN Rail",
                            e.target.checked,
                            4.0
                          )
                        }
                      >
                        CN Rail
                      </Checkbox>
                      $4.35 / km
                    </div>
                    <div className="carrier-option-item">
                      <Checkbox
                        checked={productForm.shipping_options_details?.carrier_shipping?.find(
                          (item: any) => item.unit === "Day & Ross"
                        )}
                        onChange={(e) =>
                          handleSetCarrierShipping(
                            "Day & Ross",
                            e.target.checked,
                            4.35
                          )
                        }
                      >
                        Day & Ross
                      </Checkbox>
                      $5.07 / km
                    </div>
                    <div className="carrier-option-item">
                      <Checkbox
                        checked={productForm.shipping_options_details?.carrier_shipping?.find(
                          (item: any) => item.unit === "J.B. Hunt"
                        )}
                        onChange={(e) =>
                          handleSetCarrierShipping(
                            "J.B. Hunt",
                            e.target.checked,
                            6.12
                          )
                        }
                      >
                        J.B. Hunt
                      </Checkbox>
                      $6.12 / km
                    </div>
                    <div className="carrier-option-item">
                      <Checkbox
                        checked={productForm.shipping_options_details?.carrier_shipping?.find(
                          (item: any) => item.unit === "FedEx"
                        )}
                        onChange={(e) =>
                          handleSetCarrierShipping(
                            "FedEx",
                            e.target.checked,
                            10.11
                          )
                        }
                      >
                        FedEx
                      </Checkbox>
                      $10.11 / km
                    </div>
                  </Space>
                </div>
              </Col>
            </Row>
          </div>
        </>
      )}
    </div>
  );
};

export default PricingStep;
